// ---------------------------------------------------------------------------
// step-2-template.tsx — Template picker step.
// Fetches manifest, shows search + 3-column grid. Selecting a tile captures
// the templateId and auto-advances to step 3.
// ---------------------------------------------------------------------------

import React, { useEffect, useState, useMemo } from 'react'
import styles from '../new-project-wizard-page.module.css'
import { useWizard } from '../wizard-state'
import { WizardBrainstormCta } from '@/features/brainstorm/entries'
import { useStudioLocale } from '@/i18n'
import type { NewProjectCopy } from '@/i18n/studio-new-project-copy'

interface TemplateInfo {
  id: string
  name: string
  thumbnail?: string
  category?: string
}

export const DEFAULT_TEMPLATE_BASE = 'https://cogo-starter.pages.dev'

/**
 * Where the template catalog lives for THIS deployment.
 *
 * Three inputs, three different meanings — and the middle one used to be lost:
 *
 *   absent (`undefined`)  → nobody decided. Use the cloud Starter origin.
 *   declared empty (`''`) → SOMEBODY DECIDED: serve the catalog from this
 *                           app's own origin. `.env.appliance` sets
 *                           `VITE_TEMPLATE_BASE=` and says so on the line
 *                           above it ("Empty = same-origin template catalog").
 *   a value               → use it.
 *
 * Collapsing empty into the cloud default contradicted that declaration at the
 * only place that reads it, so an air-gapped appliance fetched
 * `https://cogo-starter.pages.dev/apps/_manifest.json` from a box with no
 * route to it — blocked by the edge CSP after leaving the wizard looking
 * merely broken. Present-and-empty is the repo's existing convention for
 * exactly this ("so a reader can tell the decision from an oversight", the
 * same words `.env.appliance` uses for VITE_CHAT_GATEWAY_URL and
 * VITE_OPENPENCIL_IMPORT_API_URL); it only means anything if the reader can
 * tell the two apart.
 */
export function resolveTemplateBase(value: string | undefined | null): string {
  if (value === undefined || value === null) return DEFAULT_TEMPLATE_BASE
  const trimmed = value.trim()
  return trimmed ? trimmed.replace(/\/+$/, '') : ''
}

export const TEMPLATE_BASE = resolveTemplateBase(
  import.meta.env.VITE_TEMPLATE_BASE as string | undefined,
)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isTemplateManifest(value: unknown): value is {
  apps: Array<{
    id: string
    name?: string
    thumbnail?: string
    category?: string
  }>
} {
  if (!isRecord(value) || !Array.isArray(value.apps)) return false
  return value.apps.every((app) => {
    if (!isRecord(app) || typeof app.id !== 'string' || !app.id.trim()) {
      return false
    }
    return (
      (app.name === undefined || typeof app.name === 'string') &&
      (app.thumbnail === undefined || typeof app.thumbnail === 'string') &&
      (app.category === undefined || typeof app.category === 'string')
    )
  })
}

export async function fetchManifest(
  copy: NewProjectCopy['step2Template'],
): Promise<TemplateInfo[]> {
  const manifestUrl = `${TEMPLATE_BASE}/apps/_manifest.json`
  const res = await fetch(manifestUrl)
  if (!res.ok) throw new Error(copy.manifestFailed)

  let payload: unknown
  try {
    payload = await res.json()
  } catch {
    throw new Error(`${copy.manifestFailed}: ${copy.invalidJsonResponse}`)
  }

  if (!isTemplateManifest(payload)) {
    throw new Error(`${copy.manifestFailed}: ${copy.invalidManifestFormat}`)
  }

  const j = payload
  return j.apps.map((a) => {
    const id = a.id.trim()
    return {
      id,
      name: a.name?.trim() ? a.name : id,
      thumbnail: a.thumbnail ? `${TEMPLATE_BASE}${a.thumbnail}` : undefined,
      category: a.category,
    }
  })
}

export function Step2Template() {
  const copy = useStudioLocale().strings.newProject.step2Template
  const { state, dispatch } = useWizard()
  const [templates, setTemplates] = useState<TemplateInfo[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    fetchManifest(copy)
      .then(setTemplates)
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : copy.loadFailed),
      )
  }, [copy])

  // Derive unique categories from manifest
  const categories = useMemo(() => {
    if (!templates) return []
    const cats = new Set<string>()
    for (const t of templates) {
      if (t.category) cats.add(t.category)
    }
    return Array.from(cats).sort()
  }, [templates])

  // Filtered list
  const filtered = useMemo(() => {
    if (!templates) return []
    const q = query.toLowerCase()
    return templates.filter((t) => {
      const matchesQuery = !q || t.name.toLowerCase().includes(q) || t.id.toLowerCase().includes(q)
      const matchesCat = !selectedCategory || t.category === selectedCategory
      return matchesQuery && matchesCat
    })
  }, [templates, query, selectedCategory])

  function handleSelect(t: TemplateInfo) {
    dispatch({ type: 'SELECT_TEMPLATE', id: t.id, name: t.name })
    setTimeout(() => dispatch({ type: 'NEXT' }), 80)
  }

  return (
    <div className={styles.stepContent}>
      <div className={styles.stepHeading}>
        <h1 className={styles.stepH1}>{copy.heading}</h1>
        <p className={styles.stepLead}>{copy.lead}</p>
      </div>

      {/* Search */}
      <div className={styles.templateSearchRow}>
        <input
          type="search"
          placeholder={copy.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={styles.searchInput}
          aria-label={copy.searchLabel}
          data-testid="new-project-template-search"
        />
        {/* Brainstorm escape hatch — lets the user peek at alternative
            Starters that aren't in the default manifest grid. Mock
            provider today (Workstream C); Workstream D wires real
            findCandidates. */}
        <WizardBrainstormCta
          pattern={selectedCategory ?? undefined}
        />
      </div>

      {/* Category chips */}
      {categories.length > 0 && (
        <div className={styles.categoryChips}>
          <button
            type="button"
            className={[styles.chip, !selectedCategory ? styles.chipActive : ''].filter(Boolean).join(' ')}
            onClick={() => setSelectedCategory(null)}
          >
            {copy.all}
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={[styles.chip, selectedCategory === c ? styles.chipActive : ''].filter(Boolean).join(' ')}
              onClick={() => setSelectedCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* States */}
      {loadError && (
        <div className={styles.errorBanner} role="alert">
          {loadError}
        </div>
      )}

      {!templates && !loadError && (
        <div className={styles.loadingRow}>
          <span className={styles.spinner} aria-hidden="true" />
          {copy.loading}
        </div>
      )}

      {templates && filtered.length === 0 && (
        <div className={styles.emptyState}>
          {query ? copy.emptyFiltered(query) : copy.emptyNone}
        </div>
      )}

      {/* Grid */}
      {filtered.length > 0 && (
        <div className={styles.templateGrid}>
          {filtered.map((t) => {
            const isSelected = state.selectedTemplateId === t.id
            return (
              <button
                key={t.id}
                type="button"
                className={[
                  styles.templateTile,
                  isSelected ? styles.templateTileSelected : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleSelect(t)}
                aria-pressed={isSelected}
                data-testid={`new-project-template-${t.id}`}
              >
                <div className={styles.templateThumb}>
                  <TemplateThumbnail template={t} />
                  <div className={styles.templateHoverOverlay} aria-hidden="true">
                    {copy.preview}
                  </div>
                </div>
                <div className={styles.templateTileMeta}>
                  <span className={styles.templateTileName}>{t.name}</span>
                  {t.category && (
                    <span className={styles.templateTileCategory}>{t.category}</span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TemplateThumbnail({ template }: { template: TemplateInfo }) {
  const [imageOk, setImageOk] = useState(Boolean(template.thumbnail))

  if (template.thumbnail && imageOk) {
    return (
      <img
        src={template.thumbnail}
        alt=""
        loading="lazy"
        className={styles.templateThumbImg}
        onError={() => setImageOk(false)}
      />
    )
  }

  return (
    <div className={styles.templateThumbPlaceholder}>
      {template.name.slice(0, 2).toUpperCase()}
    </div>
  )
}
